/**
 * Lazy-loaded Excel utility wrapper
 *
 * This wrapper dynamically imports ExcelJS only when needed,
 * preventing the large library from being included in the main bundle.
 * This eliminates the eval warning and reduces initial bundle size.
 */

import type { CellValue, Column } from "exceljs";

let excelJsModule: typeof import("exceljs") | null = null;

/**
 * Lazy loads the ExcelJS module
 */
async function loadExcelJS(): Promise<typeof import("exceljs")> {
  if (!excelJsModule) {
    excelJsModule = await import("exceljs");
  }
  return excelJsModule;
}

/**
 * Creates a new Workbook instance (lazy loaded)
 */
export async function createWorkbook() {
  const ExcelJS = await loadExcelJS();
  return new ExcelJS.Workbook();
}

/**
 * exceljs-hardened@5 types `Column.values` as `CellValue`, but the runtime
 * value is a sparse `CellValue[]` (same as exceljs@4). Normalize for callers.
 */
export function getColumnValues(column: Partial<Column> | undefined): CellValue[] {
  const values = column?.values as unknown;
  return Array.isArray(values) ? (values as CellValue[]) : [];
}

/**
 * Type exports for convenience
 * These are type-only imports and won't bundle the ExcelJS code
 */
export type { Workbook, Worksheet, Row, Cell, Column, RichText, CellValue } from "exceljs";
